import { User01 } from '@untitledui/icons'
import { usePress } from 'react-aria'

import { Avatar } from '@components/designSystem/Avatar'
import Button from '@components/designSystem/Button'
import { BadgeWithFlag } from '@components/designSystem/badges/badges'
import useTypedTranslation from '@hooks/useTypedTranslation'
import DictionaryAttribution from '@routes/Account/DictionaryAttribution'
import SignOutButton from '@routes/Account/SignOutButton'
import { useStoreGrants } from '@state/store.ts'
import { type StoreGrant, useCurrentUser } from '@state/user.ts'
import { VERSION } from '@state/version.ts'
import { routesFactory } from '@utils/routes.ts'

function AccountRouteDesktop() {
  const { t } = useTypedTranslation()

  const { fullname, email } = useCurrentUser()
  const { data: storeGrants } = useStoreGrants()

  return (
    <div className='absolute inset-0 flex flex-col gap-12 overflow-y-auto p-6'>
      <div className='flex items-center gap-6'>
        <Avatar size='3xl' />
        <div className='flex flex-col'>
          <p className='text-xl font-medium'>{fullname}</p>
          <p className='font-medium text-secondary'>{email}</p>
        </div>
      </div>
      <div className='grid grid-cols-[minmax(100px,350px)_1fr] gap-y-12'>

        {/* Language Selection */}
        <Label label={t('account.language.label')} />
        <div className='flex gap-4'>
          <ChangeLanguageBadge lang='de' />
          <ChangeLanguageBadge lang='en' />
        </div>

        {/* Assigned Stores */}
        <Label label={t('account.storeAssignments.label')} />
        <div className='flex flex-col gap-4'>
          {storeGrants.length === 0 && <p className='font-medium text-secondary'>{t('account.storeAssignments.noAssignmentsHint')}</p>}
          {storeGrants.map((grant) => <StoreAssignment key={grant.store.id} grant={grant} />)}
        </div>

        {/* Version */}
        <Label label={t('account.version.label')} />
        <p className='font-mono font-bold'>{VERSION}</p>

        {/* Licences */}
        <Label label={t('account.licenses.label')} />
        <DictionaryAttribution className='max-w-2xl text-md text-secondary' />
      </div>

      <div className='mt-6'>
        <SignOutButton />
      </div>
    </div>
  )
}

function Label({ label }: {label: string}) {
  return <p className='text-md font-medium text-secondary'>{label}</p>
}

function StoreAssignment({ grant }: {grant: StoreGrant}) {
  const { t } = useTypedTranslation()
  const address = grant.store.address ? `${grant.store.address.addressLines[0]}, ${grant.store.address.city}` : t('account.storeAssignments.noAddressAvailable')

  return (
    <div className='text-md font-medium'>
      <Button className='text-primary' size='lg' variant='link-color' href={routesFactory.home(grant.store.id)}>{grant.store.name}</Button> <span className={`
        text-secondary
      `}>| {address}</span>
      <p className='flex items-center gap-2 text-secondary'>
        <User01 size={16}/>
        {t(`common.roles.store.${grant.role}`)}
      </p>
    </div>
  )
}

function ChangeLanguageBadge({ lang }: {lang: 'en' | 'de'}) {
  const { t, i18n } = useTypedTranslation()

  const { pressProps } = usePress({ onPress: () => i18n.changeLanguage(lang) })

  return (
    <button {...pressProps} className='cursor-pointer'>
      <BadgeWithFlag size='xl' flag={lang} color={i18n.resolvedLanguage === lang ? 'light' : 'gray'}>
        {t(`account.language.options.${lang}`)}
      </BadgeWithFlag>
    </button>
  )

}

export default AccountRouteDesktop